import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { GridClientContactsComponent } from '@pages/client/grid-client-contacts/grid-client-contacts.component';
import { GridCompanyListComponent } from '@pages/client/grid-company-list/grid-company-list.component';
import { FieldContainerComponent } from '@shared/components/field-container/field-container.component';
import { TextReadonlyComponent } from '@shared/components/text-readonly/text-readonly.component';
import { FINAL_ADMINISTRATION_FEE_MAX } from '@shared/constants/otic.constants';
import { ModeEnum } from '@shared/enums/modeEnum';
import { ParameterEnum } from '@shared/enums/parameterEnum';
import { BusinessArea } from '@shared/interfaces/businessArea';
import { Client } from '@shared/interfaces/client';
import { Otic } from '@shared/interfaces/otic';
import { MessageProvider } from '@shared/providers/message.provider';
import { OticService } from '@shared/services/otic.service';
import { ParameterService } from '@shared/services/parameter.service';
import { disableAllField } from '@shared/util/formUtil';
import { normalizeForSearch } from '@shared/util/stringUtils';
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { AutoCompleteCompleteEvent, AutoCompleteModule } from 'primeng/autocomplete';
import { DropdownModule } from 'primeng/dropdown';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { RadioButtonModule } from 'primeng/radiobutton';

@Component({
  selector: 'app-data-commercial-form',
  standalone: true,
  imports: [
    CommonModule,
    GridClientContactsComponent,
    GridCompanyListComponent,
    ReactiveFormsModule,
    InputTextModule,
    InputNumberModule,
    RadioButtonModule,
    DropdownModule,
    FieldContainerComponent,
    NgxMaskDirective, 
    NgxMaskPipe,
    AutoCompleteModule,
    TextReadonlyComponent
  ],
  providers: [provideNgxMask()],
  templateUrl: './data-commercial-form.component.html'
})
export class DataCommercialFormComponent implements OnInit, OnChanges {
  @ViewChild('clientContacts') clientContacts!: GridClientContactsComponent;
  @ViewChild('companyList') companyList!: GridCompanyListComponent;
  
  public form: FormGroup = this.fb.group({
    comercialName: ['', [Validators.required]],
    category: [[]],
    region: [''],
    observations: [''],
    proveniencia: [''],
    percentageKnownAnnual: [''],
    finalAdministrationFee: ['', [Validators.max(FINAL_ADMINISTRATION_FEE_MAX)]]
  });

  @Input()
  mode: ModeEnum = ModeEnum.EDIT;
  @Input() client: Client = {};
  region: any = [];
  proveniencia: any = [];
  currentOticName: Otic[] = [];
  businessAreas: BusinessArea[] = [];
  filteredBusinessAreas: BusinessArea[] = [];

  constructor(
    private fb: FormBuilder, 
    private messageProvider: MessageProvider,
    private parameterService: ParameterService,
    private oticService: OticService
  ){}

  ngOnInit(): void {
    this.parameterService.getParameter(ParameterEnum.Region).subscribe(resp => this.region = resp.data);
    this.parameterService.getParameter(ParameterEnum.Proveniencia).subscribe(resp => this.proveniencia = resp.data);
    this.oticService.getListOtic().subscribe(data => this.currentOticName = data);
    this.oticService.getListBusinessArea().subscribe(data => this.businessAreas = data);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['client']){
      const isRelatedCompaniesControl = this.form.get('isRelatedCompanies');
      const relatedCompanyNameControl = this.form.get('relatedCompanyName');
      isRelatedCompaniesControl?.valueChanges.subscribe((value: string) => {
        if (value === 'Si') {
          isRelatedCompaniesControl?.setValue('Si', { emitEvent: false });
          relatedCompanyNameControl?.enable();
        } else {
          isRelatedCompaniesControl?.setValue('No', { emitEvent: false });
          relatedCompanyNameControl?.disable();
        }
      });
      if (!this.client.isPotential){
        disableAllField(this.form, ['observations', 'proveniencia']);
      }
      this.form.reset(this.client);
    }
    if (this.mode === ModeEnum.VIEW){
      disableAllField(this.form);
    }
  }

  searchCategories(event: AutoCompleteCompleteEvent) {
    const filtered = this.businessAreas.filter(
      (item: BusinessArea) => normalizeForSearch(item.description).includes(normalizeForSearch(event.query)));
    this.filteredBusinessAreas = filtered.slice(0, 10);
  }

  validate(){
    if (this.mode != ModeEnum.EDIT) return true;
    this.form.markAllAsTouched();
    if (this.form.valid && this.companyList.getCompanyList().length === 0){
      this.messageProvider.showError('Error', 'Debe ingresar por lo menos una Empresa asociada');
      return false;
    }
    return this.form.valid;
  }
  
  getData(){
    return {
      ...this.form.value,
      companyContacts: this.clientContacts.getCompanyContacts(),
      comercialChildren: this.companyList.getCompanyList(),
      statusInPresenteYear: (this.client.isPotential ? 'Potencial' : 'Cliente Antiguo')
    };
  }

}
